import { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  ChartBarIcon,
  GiftIcon,
  BuildingOfficeIcon,
  Cog6ToothIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  UsersIcon
} from '@heroicons/react/24/solid';
import { useAuth } from '../../context/AuthContext';

const OrganizationNavLink = ({ to, icon, children }) => (
  <NavLink
    to={to}
    end
    className={({ isActive }) =>
      `flex items-center px-4 py-3 text-lg font-medium rounded-lg transition-colors duration-200 ${
        isActive
          ? 'bg-orange-500 text-white shadow-md'
          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
      }`
    }
  >
    {icon}
    <span className="ml-3">{children}</span>
  </NavLink>
);

export default function OrganizationDashboardLayout() {
  const { user, logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const location = useLocation();

  const orgName = user?.organizationName || 'Organization';
  const email = user?.email || 'No email found';
  const initials = orgName.substring(0, 2).toUpperCase();

  // Handle window resize to detect mobile/desktop
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      
      // On desktop, always show sidebar
      if (!mobile) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };

    // Initial check
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine sidebar classes based on device and state
  const getSidebarClasses = () => {
    if (isMobile) {
      return isSidebarOpen 
        ? 'translate-x-0' 
        : '-translate-x-full';
    } else {
      return 'translate-x-0'; // Always visible on desktop
    }
  };

  // Determine main content classes based on device and state
  const getMainContentClasses = () => {
    if (isMobile) {
      return 'md:ml-0'; // No margin on mobile
    } else {
      return 'md:ml-64'; // Margin for sidebar on desktop
    }
  };

  return (
    <div className="min-h-screen flex bg-gray-100 dark:bg-gray-900">
      {/* --- Sidebar --- */}
      <aside
        className={`fixed inset-y-0 left-0 z-20 w-64 flex-shrink-0 bg-white dark:bg-gray-800 shadow-lg flex flex-col transition-transform duration-300 ease-in-out ${getSidebarClasses()}`}
      >
        {/* Close Button - Visible only on mobile */}
        {isMobile && isSidebarOpen && (
          <div className="md:hidden absolute top-4 right-4">
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="p-2 rounded-md text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        )}

        <div className="h-20 flex items-center justify-center border-b border-gray-200 dark:border-gray-700 px-4">
          <span className="text-2xl font-bold text-orange-500">Give Tech to the Blind</span>
        </div>
        <nav className="flex-grow p-4 space-y-2">
          <OrganizationNavLink to="/organization/dashboard" icon={<ChartBarIcon className="w-6 h-6" />}>Dashboard</OrganizationNavLink>
          <OrganizationNavLink to="/organization/status" icon={<GiftIcon className="w-6 h-6" />}>Campaign Status</OrganizationNavLink>
          <OrganizationNavLink to="/organization/profile" icon={<BuildingOfficeIcon className="w-6 h-6" />}>Organization Profile</OrganizationNavLink>
          <OrganizationNavLink to="/organization/team" icon={<UsersIcon className="w-6 h-6" />}>Team Members</OrganizationNavLink>
          <OrganizationNavLink to="/organization/settings" icon={<Cog6ToothIcon className="w-6 h-6" />}>Settings</OrganizationNavLink>
        </nav>
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">
              {initials}
            </div>
            <div>
              <p className="font-semibold text-gray-800 dark:text-gray-200 truncate">{orgName}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full mt-4 flex items-center justify-center px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/50 transition-colors"
          >
            <ArrowLeftOnRectangleIcon className="w-5 h-5" />
            <span className="ml-2">Log Out</span>
          </button>
        </div>
      </aside>

      {/* Toggle Button - Visible only on mobile */}
      {isMobile && (
        <div className="md:hidden fixed top-4 left-4 bg-gray-200 dark:bg-gray-700 p-2 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 z-10">
          <button onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
            <Bars3Icon className="w-6 h-6 text-gray-700 dark:text-gray-300" />
          </button>
        </div>
      )}

      <main
        className={`flex-grow p-6 md:p-10 overflow-auto pt-20 md:pt-10 ${getMainContentClasses()}`}
      >
        <Outlet />
      </main>
    </div>
  );
}